import React from 'react'
import Box from '@material-ui/core/Box'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import EventAvailableOutlinedIcon from 'components/material-symbol/icons/EventAvailable'
import SectionEmptyState from 'components/section-empty-state'
import { makeStyles } from '@material-ui/core/styles'
import CustomAvailabilityDestroyDialog from '../custom-availability/custom-availability-destroy-dialog'
import { formatOverrideDate, formatTimeRange } from './utils'
import { availabilityDot } from './availability-colors'

const useStyles = makeStyles(theme => ({
  list: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: theme.palette.common.white,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid #E0E0E0',
    '&:last-child': { borderBottom: 'none' },
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 2,
    ...availabilityDot('override'),
    boxSizing: 'border-box',
    display: 'inline-block',
    flexShrink: 0,
  },
  date: {
    fontWeight: 500,
    color: '#172024',
  },
  meta: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    marginLeft: 'auto',
    textAlign: 'right',
  },
  facility: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
  },
  actions: {
    marginLeft: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  deleteButton: {
    padding: 6,
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #C4C7C9',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: '#F4F6F8',
      borderColor: theme.palette.primary.main,
    },
    '& .MuiSvgIcon-root': {
      width: 18,
      height: 18,
      fontSize: 18,
    },
  },
}))

const OverridesList = ({ overrides, onChanged, emptyAction }) => {
  const classes = useStyles()
  if (!overrides || overrides.length === 0) {
    return (
      <SectionEmptyState
        Icon={EventAvailableOutlinedIcon}
        title="No Open Time"
        caption="Add extra bookable hours on a specific day, outside your recurring weekly schedule."
        action={emptyAction}
      />
    )
  }
  return (
    <Box className={classes.list}>
      {overrides.map(override => {
        const sortedSlots = [...(override.timeSlots || [])].sort((a, b) =>
          a.startTime < b.startTime ? -1 : a.startTime > b.startTime ? 1 : 0,
        )
        const timeRanges = sortedSlots
          .map(ts => formatTimeRange(ts.startTime, ts.endTime))
          .join(', ')
        const facility = override.coachFacility?.name
        return (
          <Box key={override.id} className={classes.row}>
            <span className={classes.dot} aria-hidden />
            <Box>
              <Typography variant="body2" className={classes.date}>
                {formatOverrideDate(override.date)}
              </Typography>
              {facility && (
                <Typography variant="caption" className={classes.facility}>
                  {facility}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" className={classes.meta}>
              {timeRanges}
            </Typography>
            <Box className={classes.actions}>
              <CustomAvailabilityDestroyDialog
                override={override}
                onSuccess={onChanged}
                trigger={
                  <IconButton
                    aria-label="delete override"
                    className={classes.deleteButton}
                  >
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                }
              />
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default OverridesList
